import { Link } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { MudCarpFish } from "@/components/MudCarpFish";
import { ChatOverlay } from "@/components/ChatOverlay";
import { Bubbles } from "@/components/Bubbles";

export function Landing() {
  const reduced = useReducedMotion();

  return (
    <>
      {/* HERO */}
      <section className="relative overflow-hidden border-b-[3px] border-carp-ink">
        <ChatOverlay lanes={5} className="opacity-60" />
        <Bubbles count={18} />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-24 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7 relative">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.4 }}
              className="chip mb-4"
            >
              <span className="inline-block w-2 h-2 rounded-full bg-carp-primary animate-pulse" />
              NA ŻYWO · KARP MÓWI
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.05 }}
              className="font-display font-bold text-5xl sm:text-6xl lg:text-7xl leading-[0.9] tracking-tighter text-balance"
            >
              Sprzedajemy{" "}
              <span className="relative inline-block">
                <span className="relative z-10 text-carp-primary">odpowiedzi</span>
                <span
                  aria-hidden
                  className="absolute -inset-1 -z-0 -rotate-1 bg-carp-yellow"
                />
              </span>{" "}
              MudCarpa.
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.15 }}
              className="mt-6 text-xl sm:text-2xl text-carp-ink/85 max-w-2xl text-balance leading-snug"
            >
              Wpłacasz hajs. Piszesz co chcesz usłyszeć. Karp czyta na żywo, swoim głosem,
              w swoim stylu —{" "}
              <span className="bg-carp-ink text-carp-yellow px-2 py-0.5 font-bold">
                bez owijania w bawełnę
              </span>
              .
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.25 }}
              className="mt-8 flex flex-wrap gap-3"
            >
              <Link to="/buy" className="btn-primary text-lg">
                Zagaduj karpia
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                  <path d="M5 12h14M13 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </Link>
              <a href="#jak-to-dziala" className="btn-ghost text-lg">
                Jak to działa
              </a>
            </motion.div>

            <motion.ul
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4, duration: 0.6 }}
              className="mt-8 flex flex-wrap gap-2 text-sm"
            >
              <li className="chip bg-carp-yellow">5 PLN minimum</li>
              <li className="chip bg-carp-cyan">10 000 PLN sufit</li>
              <li className="chip">BLIK · karta · P24</li>
              <li className="chip bg-carp-lime">Faktura na życzenie</li>
            </motion.ul>
          </div>

          <div className="lg:col-span-5 relative">
            <motion.div
              animate={reduced ? undefined : { y: [0, -10, 0] }}
              transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
              className="relative mx-auto max-w-md"
            >
              <div className="brut bg-carp-paper p-6 sm:p-8 -rotate-2">
                <MudCarpFish intensity={4} size={0.85} talking ariaHidden={false} />
                <div className="mt-3 font-mono text-xs text-carp-ink/70">
                  <span className="text-carp-lime">●</span> ONLINE · feed Stripe ↔ Kick
                </div>
              </div>
              <motion.div
                animate={reduced ? undefined : { rotate: [0, 8, -4, 0] }}
                transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
                className="absolute -top-6 -right-4 brut bg-carp-yellow px-3 py-2 rotate-6"
              >
                <span className="font-display font-bold text-sm uppercase">
                  „Powiem ci coś, co cię zaboli"
                </span>
              </motion.div>
            </motion.div>
          </div>
        </div>

        {/* yellow caution stripe at bottom of hero */}
        <div aria-hidden className="stripe h-3 w-full" />
      </section>

      {/* TICKER */}
      <section
        aria-hidden
        className="bg-carp-ink text-carp-paper py-4 overflow-hidden border-b-[3px] border-carp-ink"
      >
        <div className="ticker">
          {Array.from({ length: 2 }).map((_, k) => (
            <div
              key={k}
              className="flex shrink-0 items-center gap-10 px-10 font-display font-bold text-2xl uppercase tracking-wide"
            >
              <span>karp w sprzedaży</span>
              <span className="text-carp-primary">●</span>
              <span>twoja wiadomość — jego głos</span>
              <span className="text-carp-yellow">●</span>
              <span>bez moderacji</span>
              <span className="text-carp-cyan">●</span>
              <span>bez zwrotów</span>
              <span className="text-carp-lime">●</span>
              <span>5 zł — 10 000 zł</span>
              <span className="text-carp-secondary">●</span>
            </div>
          ))}
        </div>
      </section>

      {/* JAK TO DZIAŁA */}
      <section id="jak-to-dziala" className="relative py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="max-w-2xl">
            <p className="chip mb-3">CZTERY KROKI</p>
            <h2 className="font-display font-bold text-4xl sm:text-5xl tracking-tight">
              Jak to się odbywa,{" "}
              <span className="bg-carp-ink text-carp-paper px-2">krok po kroku</span>?
            </h2>
            <p className="mt-3 text-lg text-carp-ink/80">
              Bez bzdur. Bez subskrypcji. Bez aplikacji. Bez konta. Bez kontekstu.
            </p>
          </div>

          <div className="mt-12 grid gap-6 lg:grid-cols-4 sm:grid-cols-2">
            {STEPS.map((step, i) => (
              <Step key={step.title} idx={i + 1} {...step} />
            ))}
          </div>
        </div>
      </section>

      {/* WARTOŚCI / "FEATURES" */}
      <section className="relative py-16 sm:py-24 bg-carp-paper border-y-[3px] border-carp-ink">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 grid lg:grid-cols-3 gap-6">
          <FeatureCard
            tone="rose"
            title="Twoja wiadomość. Jego głos."
            body="Piszesz max 500 znaków. Karp czyta na żywo, w swoim stylu — sarkastycznym jak kumpel z baru o 2 w nocy."
          />
          <FeatureCard
            tone="blue"
            title="Bez filtra. Z umiarem."
            body="Karp ma swoje opinie i potrafi pyskować. Nie wezwie do nienawiści, ale nie obieca że będzie miło."
          />
          <FeatureCard
            tone="yellow"
            title="Bez zwrotów. Bez wkrętów."
            body="Treść cyfrowa, dostarczana od razu. Akceptujesz zgodę z art. 38 ust. 13 i tracisz prawo odstąpienia. Pieniądze idą, słowa lecą."
          />
        </div>
      </section>

      {/* PRZEDSPRZEDAŻ 10K — fizyczna ryba + roczna subskrypcja */}
      <PreorderSection />

      {/* CTA */}
      <section className="relative py-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <div className="brut bg-carp-primary text-white p-8 sm:p-14 text-center relative overflow-hidden">
            <div aria-hidden className="absolute -top-10 -right-10 w-48 h-48 rotate-12 bg-carp-yellow opacity-20" />
            <h3 className="font-display font-bold text-4xl sm:text-5xl tracking-tight text-shadow-brut">
              No to dawaj, mordo.
            </h3>
            <p className="mt-4 text-lg sm:text-xl max-w-2xl mx-auto text-balance">
              5 zł — usłyszysz słówko. 10 000 zł — usłyszysz monolog stulecia.
              Karp jest cierpliwy, ale głodny.
            </p>
            <div className="mt-8 flex justify-center">
              <Link to="/buy" className="btn-accent text-lg !bg-carp-ink hover:!bg-carp-paper hover:!text-carp-ink">
                Zagaduj karpia
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

const STEPS: { title: string; body: string; tone: "rose" | "blue" | "yellow" | "lime" }[] = [
  { title: "Piszesz", body: "Wiadomość, max 500 znaków. To, co chcesz żeby Karp powiedział na głos.", tone: "yellow" },
  { title: "Płacisz", body: "Od 5 do 10 000 zł. BLIK, karta lub Przelewy24. Bezpiecznie przez Stripe.", tone: "rose" },
  { title: "Karp gada", body: "Twoja wiadomość trafia do MudCarpa, on układa odpowiedź i czyta ją na streamie.", tone: "blue" },
  { title: "Słuchasz", body: "Stream Kick → ESP32 → fizyczna ryba → głośnik. Słychać i widać.", tone: "lime" },
];

function Step({ idx, title, body, tone }: { idx: number; title: string; body: string; tone: "rose" | "blue" | "yellow" | "lime" }) {
  const bg =
    tone === "rose" ? "bg-carp-secondary text-carp-ink"
    : tone === "blue" ? "bg-carp-accent text-white"
    : tone === "yellow" ? "bg-carp-yellow text-carp-ink"
    : "bg-carp-lime text-carp-ink";

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.4, delay: idx * 0.06 }}
      whileHover={{ rotate: idx % 2 === 0 ? -1.5 : 1.5 }}
      className={`brut ${bg} p-6 relative`}
    >
      <div className="absolute -top-5 -left-5 brut bg-carp-paper text-carp-ink w-12 h-12 flex items-center justify-center font-display font-bold text-2xl">
        {idx}
      </div>
      <h4 className="mt-2 font-display font-bold text-2xl">{title}</h4>
      <p className="mt-2 text-base leading-snug">{body}</p>
    </motion.div>
  );
}

function FeatureCard({ tone, title, body }: { tone: "rose" | "blue" | "yellow"; title: string; body: string }) {
  const bg =
    tone === "rose"
      ? "bg-carp-primary text-white"
      : tone === "blue"
        ? "bg-carp-accent text-white"
        : "bg-carp-yellow text-carp-ink";
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      whileHover={{ y: -4 }}
      transition={{ duration: 0.3 }}
      className={`brut p-6 ${bg}`}
    >
      <h4 className="font-display font-bold text-2xl">{title}</h4>
      <p className="mt-3 text-base leading-snug">{body}</p>
    </motion.div>
  );
}

function PreorderSection() {
  const reduced = useReducedMotion();
  return (
    <section
      id="przedsprzedaz"
      aria-labelledby="preorder-title"
      className="relative py-16 sm:py-24 overflow-hidden"
    >
      {/* tło: żółto-czarne paski po prawej stronie */}
      <div
        aria-hidden
        className="absolute inset-y-0 right-0 w-1/3 stripe opacity-15 pointer-events-none"
      />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6">
        <div className="grid lg:grid-cols-12 gap-8 items-center">
          {/* PRAWY blok — wizualizacja */}
          <div className="lg:col-span-5 lg:order-2 relative">
            {/* Stempel "PRZEDSPRZEDAŻ" */}
            <motion.div
              animate={reduced ? undefined : { rotate: [-8, -10, -8] }}
              transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
              className="absolute -top-4 -left-4 sm:-left-8 z-10 brut bg-carp-yellow px-4 py-2 -rotate-12"
            >
              <span className="font-display font-bold text-xl sm:text-2xl uppercase tracking-tight">
                Przedsprzedaż
              </span>
            </motion.div>

            {/* Karta z rybą na "podstawce" */}
            <div className="brut bg-carp-paper p-6 sm:p-8 rotate-2 scan">
              <div className="aspect-[4/3] flex items-end justify-center">
                <motion.div
                  animate={reduced ? undefined : { y: [0, -6, 0] }}
                  transition={{ duration: 3.5, repeat: Infinity, ease: "easeInOut" }}
                >
                  <MudCarpFish intensity={4} size={0.95} talking ariaHidden={false} />
                </motion.div>
              </div>
              {/* Podstawka pod rybę */}
              <div className="mt-2 mx-auto h-3 w-3/4 bg-carp-ink" />
              <div className="mx-auto h-2 w-1/2 bg-carp-ink mt-1" />

              <div className="mt-4 grid grid-cols-2 gap-2 text-xs font-mono">
                <span className="chip bg-carp-cyan">ESP32-S3</span>
                <span className="chip bg-carp-lime">mmWave radar</span>
                <span className="chip bg-carp-secondary">I2S audio</span>
                <span className="chip bg-carp-yellow">on-device WakeNet</span>
              </div>
            </div>

            {/* Kalendarzowa naklejka */}
            <motion.div
              animate={reduced ? undefined : { rotate: [6, 9, 6] }}
              transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
              className="absolute -bottom-4 -right-2 sm:-right-6 brut bg-white p-3 sm:p-4 rotate-6"
            >
              <p className="font-mono text-[10px] uppercase tracking-wide text-carp-ink/70">
                Release
              </p>
              <p className="font-display font-bold text-3xl sm:text-4xl leading-none">
                <span className="text-carp-primary">1</span>{" "}
                <span>IX</span>
              </p>
              <p className="font-mono text-[10px] uppercase tracking-wide text-carp-ink/70 mt-1">
                2026
              </p>
            </motion.div>
          </div>

          {/* LEWY blok — copy */}
          <div className="lg:col-span-7 lg:order-1">
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.4 }}
            >
              <p className="chip bg-carp-ink text-carp-yellow mb-3">
                10 000 zł = fizyczny karp + rok gadania
              </p>
              <h2
                id="preorder-title"
                className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[0.95] text-balance"
              >
                Wpłacasz dychę kafla — karp przyjeżdża{" "}
                <span className="bg-carp-ink text-carp-paper px-2">na półkę</span>.
              </h2>

              <p className="mt-5 text-lg sm:text-xl text-carp-ink/85 max-w-2xl text-balance leading-snug">
                Każdy donejt na{" "}
                <strong className="text-carp-primary">10 000 zł</strong>{" "}
                to{" "}
                <strong>przedsprzedaż MudCarp Device</strong> — fizyczna ryba
                z głośnikiem i mikrofonem, plus{" "}
                <strong>roczna subskrypcja odpowiedzi</strong> w cenie.
              </p>

              <div className="mt-6 grid sm:grid-cols-2 gap-3">
                <PreorderBullet
                  label="W zestawie"
                  text="MudCarp v1 (ESP32-S3 + mmWave + głośnik)"
                  tone="rose"
                />
                <PreorderBullet
                  label="W subskrypcji"
                  text="365 dni — Karp odpowiada na Twoje zagadania bez limitu"
                  tone="blue"
                />
                <PreorderBullet
                  label="Wysyłka"
                  text="Polska, kurier; międzynarodowo — pisz na maila"
                  tone="yellow"
                />
                <PreorderBullet
                  label="Release"
                  text="1 września 2026 — pierwszy dzwonek, pierwszy karp"
                  tone="lime"
                />
              </div>

              <div className="mt-6 brut bg-carp-yellow p-4 sm:p-5 -rotate-1 max-w-2xl">
                <p className="font-display font-bold text-lg sm:text-xl leading-snug text-balance">
                  Oficjalny release{" "}
                  <span className="text-carp-primary">1 września</span> — w sam
                  raz na powrót do szkoły. Bo nie ma lepszej motywacji do nauki niż karp,
                  który mówi w twarz że spalisz semestr jak teraz nie usiądziesz.
                </p>
              </div>

              <div className="mt-7 flex flex-wrap items-center gap-3">
                <Link to="/buy?amount=10000" className="btn-primary text-lg">
                  Rezerwuję karpia (10 000 zł)
                </Link>
                <span className="text-xs text-carp-ink/60 font-mono max-w-xs">
                  Płatność jednorazowa. Faktura VAT w komplecie. Subskrypcja
                  startuje w dniu releasu.
                </span>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}

function PreorderBullet({
  label,
  text,
  tone,
}: {
  label: string;
  text: string;
  tone: "rose" | "blue" | "yellow" | "lime";
}) {
  const bg =
    tone === "rose"
      ? "bg-carp-secondary"
      : tone === "blue"
        ? "bg-carp-cyan"
        : tone === "yellow"
          ? "bg-carp-yellow"
          : "bg-carp-lime";
  return (
    <div className={`brut ${bg} p-3 text-carp-ink`}>
      <p className="text-[10px] font-display font-bold uppercase tracking-wide opacity-80">
        {label}
      </p>
      <p className="text-sm leading-snug font-medium mt-0.5">{text}</p>
    </div>
  );
}

export default Landing;
